/*
 * Travel charm reviews — renders the Junip-style review list under the Travel Charm builder (2026-09-16).
 * Data: the INDEX asset on <travel-charm-reviews data-src> ({total, counts, average, chunk_size, chunks,
 * reviews: [chunk 1]}) plus chunk files 2..N from data-chunks (asset URLs emitted by the section), fetched only
 * as "See more reviews" or a star filter needs them. Summary (average, count, histogram) comes from the index.
 * Reviews arrive newest first (the generator sorts); on arrival every timestamp is shifted so the newest review
 * reads as today. Strings come from data-strings (locales/en.default.json).
 * See sections/travel-charm-reviews.liquid for the markup this fills.
 */
(function () {
  'use strict';
  if (customElements.get('travel-charm-reviews')) return;

  const BODY_CUT = 220; // characters shown before "Read more" (Junip cuts long bodies at about this length)
  const DAY = 86400000;

  const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(k).join(map[k]), tpl);

  class TravelCharmReviews extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try { this.strings = JSON.parse(this.dataset.strings || '{}'); } catch (e) { this.strings = {}; }
      try { this.chunkUrls = JSON.parse(this.dataset.chunks || '[]'); } catch (e) { this.chunkUrls = []; }
      this.pageSize = Math.max(1, parseInt(this.dataset.pageSize, 10) || 5);
      this.filter = 0; // 0 = all, 1–5 = only that rating
      this.shown = 0;
      this.reviews = [];
      this.meta = null;
      this.loadedChunks = 0; // chunks fetched so far (chunk 1 rides in the index)
      this.offset = 0; // the date shift, fixed from the first chunk's newest review
      this.busy = false;
      this.$list = this.querySelector('[data-list]');
      this.$more = this.querySelector('[data-more]');
      this.$summary = this.querySelector('[data-summary]');
      this.$filterLine = this.querySelector('[data-filter-line]');
      this.$filterText = this.querySelector('[data-filter-text]');
      this.$tpl = this.querySelector('[data-card-template]');
      this.$more.addEventListener('click', () => this.renderPage());
      this.querySelector('[data-show-all]').addEventListener('click', () => this.setFilter(0));
      this.querySelectorAll('[data-bar]').forEach((b) => b.addEventListener('click', () => {
        const r = parseInt(b.dataset.bar, 10);
        this.setFilter(this.filter === r ? 0 : r);
      }));
      this.load();
    }

    async fetchJson(url) {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(res.status);
      return res.json();
    }

    async load() {
      try {
        const data = await this.fetchJson(this.dataset.src);
        if (Array.isArray(data)) {
          // a plain array (the pre-chunk format) still works as a single chunk
          this.meta = { total: data.length, chunks: 1 };
          this.addChunk(data);
        } else {
          this.meta = data;
          this.addChunk(data.reviews || []);
        }
      } catch (e) {
        this.meta = null;
      }
      if (!this.meta || !this.reviews.length) { this.hidden = true; return; }
      this.renderSummary();
      this.setFilter(0);
    }

    // A chunk arrives: shift its dates by the offset fixed from the very first review, append.
    addChunk(list) {
      const rows = (Array.isArray(list) ? list : []).filter((r) => r && r.body);
      if (!this.reviews.length && rows.length) {
        const newest = Math.max(...rows.map((r) => new Date(r.review_timestamp).getTime()).filter((x) => !isNaN(x)));
        if (isFinite(newest)) {
          // The file is static; without this the feed ages (Jorrit 2026-09-16: "do the shift on load"). The newest
          // review lands at 09:00 UTC today, or an hour ago if that is earlier, so the top card reads "N hours ago"
          // (never "Just now" on every load) and no timestamp sits in the future. Every other review keeps its
          // distance from it, so the ~60-day spread always ends on the current day.
          const now = Date.now();
          const d = new Date(now);
          const target = Math.min(now - 3600000, Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) + 9 * 3600000);
          this.offset = target - newest;
        }
      }
      rows.forEach((r) => {
        const t = new Date(r.review_timestamp).getTime();
        r.review_timestamp = isNaN(t) ? null : new Date(t + this.offset);
      });
      this.reviews = this.reviews.concat(rows);
      this.loadedChunks += 1;
    }

    get allLoaded() {
      return !this.meta || this.loadedChunks >= (this.meta.chunks || 1) || this.loadedChunks - 1 >= this.chunkUrls.length;
    }

    async loadNextChunk() {
      if (this.allLoaded) return false;
      const url = this.chunkUrls[this.loadedChunks - 1]; // chunk 2 is chunkUrls[0]
      try {
        this.addChunk(await this.fetchJson(url));
        return true;
      } catch (e) {
        this.meta.chunks = this.loadedChunks; // stop asking
        return false;
      }
    }

    get visible() {
      return this.filter ? this.reviews.filter((r) => Number(r.star_rating) === this.filter) : this.reviews;
    }

    renderSummary() {
      const m = this.meta;
      const counts = [0, 0, 0, 0, 0, 0];
      let n = 0;
      let avg = 0;
      if (m.counts) {
        for (let s = 1; s <= 5; s++) counts[s] = Number(m.counts[s]) || 0;
        n = Number(m.total) || counts.reduce((a, b) => a + b, 0);
        avg = Number(m.average) || (n ? counts.reduce((a, c, s) => a + c * s, 0) / n : 0);
      } else {
        this.reviews.forEach((r) => { const s = Math.min(5, Math.max(1, Math.round(Number(r.star_rating) || 0))); counts[s]++; });
        n = this.reviews.length;
        avg = n ? counts.reduce((a, c, s) => a + c * s, 0) / n : 0;
      }
      this.querySelector('[data-average]').textContent = avg.toFixed(2).replace(/0$/, '');
      this.stars(this.querySelector('[data-average-stars]'), avg);
      this.querySelector('[data-count]').textContent = fill(this.strings.count || '__N__', { __N__: n.toLocaleString() });
      this.counts = counts;
      this.querySelectorAll('[data-bar]').forEach((b) => {
        const r = parseInt(b.dataset.bar, 10);
        const c = counts[r];
        b.querySelector('[data-bar-fill]').style.width = (c ? Math.max(2, (c / n) * 100) : 0) + '%';
        b.disabled = !c;
        b.title = c ? fill(this.strings.filtered || '', { __N__: c.toLocaleString(), __R__: r }) : '';
      });
      this.$summary.hidden = false;
    }

    stars(el, value) {
      el.innerHTML = '';
      const rounded = Math.round(value);
      for (let i = 1; i <= 5; i++) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttribute('href', '#tcr-star');
        svg.appendChild(use);
        if (i > rounded) svg.setAttribute('class', 'is-empty');
        el.appendChild(svg);
      }
      el.setAttribute('aria-label', fill(this.strings.stars_aria || '__R__', { __R__: Number.isInteger(value) ? value : value.toFixed(1) }));
    }

    setFilter(rating) {
      this.filter = rating;
      this.shown = 0;
      this.$list.innerHTML = '';
      this.querySelectorAll('[data-bar]').forEach((b) => b.setAttribute('aria-pressed', String(parseInt(b.dataset.bar, 10) === rating)));
      if (rating) {
        // the count comes from the index, not from what happens to be loaded
        const c = this.counts ? this.counts[rating] : this.visible.length;
        this.$filterText.textContent = fill(this.strings.filtered || '', { __N__: c.toLocaleString(), __R__: rating });
        this.$filterLine.hidden = false;
      } else {
        this.$filterLine.hidden = true;
      }
      this.renderPage();
    }

    async renderPage() {
      if (this.busy) return;
      this.busy = true;
      this.$more.disabled = true;
      const filter = this.filter;
      // pull chunks until this page can be filled (a 1% star filter may need several)
      while (this.visible.length < this.shown + this.pageSize && !this.allLoaded) {
        if (!(await this.loadNextChunk())) break;
        if (this.filter !== filter) { this.busy = false; this.$more.disabled = false; return; } // filter changed mid-fetch
      }
      const list = this.visible;
      const next = list.slice(this.shown, this.shown + this.pageSize);
      if (!next.length && !this.shown) {
        const p = document.createElement('p');
        p.className = 'tcr__empty';
        p.textContent = this.strings.none || '';
        this.$list.appendChild(p);
      }
      const frag = document.createDocumentFragment();
      next.forEach((r) => frag.appendChild(this.card(r)));
      this.$list.appendChild(frag);
      this.shown += next.length;
      this.$more.hidden = this.shown >= list.length && this.allLoaded;
      this.$more.disabled = false;
      this.busy = false;
    }

    card(r) {
      const el = this.$tpl.content.firstElementChild.cloneNode(true);
      const name = String(r.reviewer_name || '').trim();
      el.querySelector('[data-initials]').textContent = name.split(/\s+/).map((w) => w[0] || '').join('').replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();
      el.querySelector('[data-name]').textContent = name.replace(/\.$/, ''); // "Karen S" like Junip, not "Karen S."
      this.stars(el.querySelector('[data-stars]'), Number(r.star_rating) || 0);
      const time = el.querySelector('[data-date]');
      const when = r.review_timestamp instanceof Date ? r.review_timestamp : null;
      if (when && !isNaN(when)) {
        time.textContent = this.relative(when);
        time.setAttribute('datetime', when.toISOString());
        time.title = when.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
      } else {
        time.remove();
      }
      const title = el.querySelector('[data-title]');
      if (r.title) title.textContent = r.title; else title.remove();
      const body = el.querySelector('[data-body]');
      const more = el.querySelector('[data-readmore]');
      const full = String(r.body || '').trim();
      if (full.length > BODY_CUT) {
        const cut = full.slice(0, BODY_CUT).replace(/\s+\S*$/, '') + '…';
        body.textContent = cut;
        more.hidden = false;
        let open = false;
        more.addEventListener('click', () => {
          open = !open;
          body.textContent = open ? full : cut;
          more.textContent = open ? (this.strings.read_less || '') : (this.strings.read_more || '');
        });
      } else {
        body.textContent = full;
      }
      const rec = el.querySelector('[data-rec]');
      const yes = r.would_recommend !== false;
      rec.classList.toggle('tcr__rec--no', !yes);
      el.querySelector('[data-rec-text]').textContent = yes ? (this.strings.recommend || '') : (this.strings.not_recommend || '');
      return el;
    }

    relative(date) {
      const s = this.strings;
      const now = new Date();
      const hours = Math.floor((now.getTime() - date.getTime()) / 3600000);
      if (hours < 1) return s.just_now || '';
      if (hours === 1) return s.hour_ago || '';
      if (hours < 24) return fill(s.hours_ago || '', { __N__: hours });
      // calendar days in UTC: the file is sorted by its UTC timestamps, so a local-time day would flip a
      // late-evening review into "Today" between two "Yesterday"s (2026-09-16)
      const startOf = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      const days = Math.max(0, Math.round((startOf(now) - startOf(date)) / DAY));
      if (days < 1) return s.today || '';
      if (days === 1) return s.yesterday || '';
      if (days < 7) return fill(s.days_ago || '', { __N__: days });
      const weeks = Math.floor(days / 7);
      if (weeks === 1) return s.week_ago || '';
      if (days < 30) return fill(s.weeks_ago || '', { __N__: weeks });
      const months = Math.floor(days / 30);
      if (months <= 1) return s.month_ago || '';
      return fill(s.months_ago || '', { __N__: months });
    }
  }

  customElements.define('travel-charm-reviews', TravelCharmReviews);
})();
